import Fuse from 'fuse.js'
import VehicleList from '@/models/vehicleList'
import Vehicle from '@/models/vehicle'

// initial state
const state = {
  loaded: false,
  vehicles: {
    items: [],
  },
  viewMode: null, // 'list' or 'grid'
  sort: null, // 'relevance', 'price_asc', 'price_desc' or 'mileage_asc'
  currentVehicleId: null,
}

// getters
const getters = {
  all: (state: any) => {
    return state.vehicles.items
  },

  hasCarSelexy: (state: any) => {
    return state.vehicles.items.some(
      (vehicle: Vehicle) =>
        vehicle.properties.type?.includes('CarSelexy') || vehicle.properties.description?.includes('CarSelexy')
    )
  },

  current: (state: any) => {
    if (state.currentVehicleId) {
      return state.vehicles.items.find((vehicle: Vehicle) => vehicle.id === state.currentVehicleId)
    }
  },

  filtered: (state: any, getters: any, rootState: any, rootGetters: any) => {
    // Set variables
    let filteredVehicles: Vehicle[] = []
    const selectedOptions = rootState.filters.selectedOptions
    let outputVehicles: Vehicle[] = []

    // Filter out words less than 3 characters
    const searchPhrase = rootState.search.searchPhrase
    const searchInSelection = rootState.search.searchInSelection

    // If there is no searchPhrase, or there is a searchPhrase and
    // the user wants to search within the selection, filter the vehicles out
    if (!searchPhrase || (searchPhrase && searchInSelection)) {
      // Filter all checked options
      state.vehicles.items.forEach((vehicle: any) => {
        let matchesAllCategories = true
        rootState.filters.filterData.otherOptions.forEach((category: any) => {
          if (category.id in selectedOptions) {
            let matchesAnOption = false
            category.options.forEach((option: any) => {
              if (
                selectedOptions[category.id].includes(option.id) &&
                vehicle.properties[category.id] &&
                vehicle.properties[category.id].toString().toLowerCase() === option.label.toString().toLowerCase()
              ) {
                matchesAnOption = true
              }
            })
            if (!matchesAnOption) {
              matchesAllCategories = false
            }
          }
        })
        if (matchesAllCategories) {
          filteredVehicles.push(vehicle)
        }
      })

      // Filter CarSelexy
      // eslint-disable-next-line no-inner-declarations
      function matchesCarSelexy(this: any, vehicle: any) {
        const carselexy = this
        if (!carselexy) return true
        return vehicle.properties.type?.includes('CarSelexy') || vehicle.properties.description?.includes('CarSelexy')
      }
      filteredVehicles = filteredVehicles.filter(matchesCarSelexy, rootState.filters.isCarselexy)
      // Filter mileage
      // eslint-disable-next-line no-inner-declarations
      function matchesMileage(this: any, vehicle: any) {
        const min = this.min
        const max = this.max
        return vehicle.properties.mileage >= min && vehicle.properties.mileage <= max
      }
      filteredVehicles = filteredVehicles.filter(matchesMileage, rootState.filters.selectedMileage)

      // Filter price
      // eslint-disable-next-line no-inner-declarations
      function matchesPrice(this: any, vehicle: any) {
        const min = this.min
        const max = this.max
        return vehicle.properties.price >= min && vehicle.properties.price <= max
      }
      filteredVehicles = filteredVehicles.filter(matchesPrice, rootState.filters.selectedPrice)
      // Filter brand
      // eslint-disable-next-line no-inner-declarations
      function matchesBrand(this: any, vehicle: any) {
        const selectedBrand = this
        if (selectedBrand === '') return true
        return vehicle.properties.brand?.toLowerCase() === selectedBrand.toLowerCase()
      }
      filteredVehicles = filteredVehicles.filter(matchesBrand, rootState.filters.selectedBrand)

      // Filter model
      // eslint-disable-next-line no-inner-declarations
      function matchesModel(this: any, vehicle: any) {
        const selectedModel = this
        if (selectedModel === '') return true
        const vehicleModel =
          vehicle.properties.model.startsWith('e-') && vehicle.properties.model !== 'e-tron'
            ? vehicle.properties.model.replace('e-', '')
            : vehicle.properties.model
        return rootState.settings.general.mergeModels
          ? vehicleModel?.toLowerCase().startsWith(selectedModel.toLowerCase().split(' ')[0])
          : vehicleModel.toLowerCase() === selectedModel.toLowerCase()
      }
      filteredVehicles = filteredVehicles.filter(matchesModel, rootState.filters.selectedModel)

      // Else => there is a searchPhrase, but user wants to search in all vehicles
    } else {
      filteredVehicles = state.vehicles.items
    }

    // Do the Fuse search
    if (searchPhrase) {
      const options = {
        keys: [
          {
            name: 'properties.brand',
            weight: 4,
          },
          {
            name: 'properties.model',
            weight: 2,
          },
          {
            name: 'properties.type',
            weight: 2,
          },
          'properties.license',
        ],
        minMatchCharLength: 2,
        includeScore: true,
        useExtendedSearch: true,
        location: 0,
        threshold: 0.4,
        distance: 100,
      }
      const fuse = new Fuse(filteredVehicles, options)
      const results = fuse.search(searchPhrase.replace(' ', '|'))

      // Loop all results and filter out the best matching (above score threshold)
      const scoreThreshold = 0
      const searchedVehicles: Vehicle[] = []
      results.forEach((item: any) => {
        searchedVehicles.push(item.item)
      })
      outputVehicles = searchedVehicles
    } else {
      outputVehicles = filteredVehicles
    }

    // Sort
    if (state.sort === 'price_asc') {
      outputVehicles = Array.from(outputVehicles).sort((a, b) => a.properties.price - b.properties.price)
    } else if (state.sort === 'price_desc') {
      outputVehicles = Array.from(outputVehicles).sort((a, b) => b.properties.price - a.properties.price)
    } else if (state.sort === 'mileage_asc') {
      outputVehicles = Array.from(outputVehicles).sort((a, b) => a.properties.mileage - b.properties.mileage)
    } else if (state.sort === 'alphabetically') {
      outputVehicles = Array.from(outputVehicles).sort((a, b) => {
        const nameA = a.properties.brand + ' ' + a.properties.model + ' ' + a.properties.model
        const nameB = b.properties.brand + ' ' + b.properties.model + ' ' + b.properties.model
        return nameA.localeCompare(nameB)
      })
    } else if (state.sort === 'date_added_desc') {
      outputVehicles = Array.from(outputVehicles).sort((a, b) => b.entryDate - a.entryDate)
    }

    return outputVehicles
  },
}

const mutations = {
  setFromAPI: (state: any, vehicles: VehicleList) => {
    state.vehicles = vehicles
    state.loaded = true
  },

  updateCurrentVehicle: (state: any, vehicleId: number) => {
    state.currentVehicleId = vehicleId
  },

  updateSorting: (state: any, sort: string) => {
    state.sort = sort
  },

  updateViewMode: (state: any, viewMode: string) => {
    state.viewMode = viewMode
  },

  toggleViewMode: (state: any) => {
    state.viewMode = state.viewMode === 'list' ? 'grid' : 'list'
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
}
